import React, { useState } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';
import { useParams } from 'react-router-dom';

import { NoraButton } from '../../../../common/components/v2';

import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { Box, Button, Divider, Drawer, Typography } from '@mui/material';
import moment from 'moment';

import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { RootState } from '../../../../app/rootReducer';
import { AppDispatch } from '../../../../app/store';
import { IconLink } from '../../../../common/components/IconLink';
import { NoraCaption } from '../../../../common/components/NoraCaption';
import {
  transformRateClass,
  transformTobaccoClass,
} from '../../../../common/constants/decision';
import { Resource } from '../../../../common/constants/resource';
import ElementLocators from '../../../../common/ElementLocators';
import { tasksResolved } from '../../../../common/helpers';
import { ITask } from '../../../tasks/interfaces/ITask';
import { getAllTasks } from '../../../tasks/slice/taskListSlice';
import { postDecisionReview } from '../../decision/decisionSlice';
import { IDecision } from '../../interfaces/IDecision';
import { InvalidDrawer } from '../InvalidDrawer';
import styles from '../styles.module.scss';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export default function ReviewDecision({ decision }: { decision: IDecision }) {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams<{ id: string }>();
  const [isOpen, setIsOpen] = useState(false);
  const [isAgreeing, setIsAgreeing] = useState(false);
  const tasks: ITask[] = useSelector(state => getAllTasks(state.taskList));
  const isValid = tasksResolved(tasks);

  function agreeToPrelim() {
    setIsOpen(true);
    setIsAgreeing(true);
  }

  function disagreeToPrelim() {
    setIsOpen(true);
    setIsAgreeing(false);
  }

  function onConfirm() {
    if (id) {
      dispatch(
        postDecisionReview(
          id,
          decision.id.toString(),
          isAgreeing ? 'Approved' : 'Denied',
        ),
      );
    }
    setIsOpen(false);
  }

  const formatDecisionValue = (value: any) => {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    return value;
  };

  const formatCoverage = (coverage: number) => {
    return coverage ? `$${coverage.toLocaleString()}` : '—';
  };

  const formatDate = (dateString: string) => {
    return moment(dateString).format('MMM DD, YYYY');
  };

  const formatTerm = (term: number) => {
    return term ? `${term} Years` : '—';
  };

  const formatDebits = (debits: number | undefined) => {
    return debits !== null && debits !== undefined ? debits.toString() : '—';
  };

  return (
    <>
      {!isValid && (
        <InvalidDrawer
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          labelCopy={Resource.REVIEW_APPLICATION}
        />
      )}
      {isValid && (
        <Drawer
          open={isOpen}
          anchor="right"
          variant="persistent"
          classes={{ paper: styles.Drawer }}
          transitionDuration={{ enter: 0, exit: 0 }}
        >
          <Box mt={6}>
            <Box
              m={3}
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body1">
                {isAgreeing
                  ? Resource.AGREE_WITH_PRELIM
                  : Resource.DISAGREE_WITH_PRELIM}
              </Typography>
              <Button onClick={() => setIsOpen(false)}>
                <Typography variant="subtitle2" color="textSecondary">
                  Cancel
                </Typography>
              </Button>
            </Box>
            <Divider />

            {/* Decision Details Section */}
            <Box m={3}>
              <Typography variant="h6" gutterBottom>
                Decision Details
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2}>
                <Box flex="1 1 calc(50% - 8px)" minWidth="200px">
                  <Typography variant="overline" color="textSecondary">
                    Decision Type
                  </Typography>
                  <Typography variant="body2">
                    {formatDecisionValue(decision.decisionType)}
                  </Typography>
                </Box>
                <Box flex="1 1 calc(50% - 8px)" minWidth="200px">
                  <Typography variant="overline" color="textSecondary">
                    Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(decision.createdAt)}
                  </Typography>
                </Box>
                <Box flex="1 1 calc(50% - 8px)" minWidth="200px">
                  <Typography variant="overline" color="textSecondary">
                    Decision
                  </Typography>
                  <Typography variant="body2">
                    {formatDecisionValue(decision.decision)}
                  </Typography>
                </Box>
                <Box flex="1 1 calc(50% - 8px)" minWidth="200px">
                  <Typography variant="overline" color="textSecondary">
                    Coverage
                  </Typography>
                  <Typography variant="body2">
                    {formatCoverage(decision.coverage)}
                  </Typography>
                </Box>
                <Box flex="1 1 calc(50% - 8px)" minWidth="200px">
                  <Typography variant="overline" color="textSecondary">
                    Term
                  </Typography>
                  <Typography variant="body2">
                    {formatTerm(decision.term)}
                  </Typography>
                </Box>
                <Box flex="1 1 calc(50% - 8px)" minWidth="200px">
                  <Typography variant="overline" color="textSecondary">
                    Class
                  </Typography>
                  <Typography variant="body2">
                    {formatDecisionValue(
                      transformRateClass(decision.rateClass),
                    )}
                  </Typography>
                </Box>
                <Box flex="1 1 calc(50% - 8px)" minWidth="200px">
                  <Typography variant="overline" color="textSecondary">
                    Tobacco
                  </Typography>
                  <Typography variant="body2">
                    {formatDecisionValue(
                      transformTobaccoClass(decision.tobaccoClass),
                    )}
                  </Typography>
                </Box>
                <Box flex="1 1 calc(50% - 8px)" minWidth="200px">
                  <Typography variant="overline" color="textSecondary">
                    Debits
                  </Typography>
                  <Typography variant="body2">
                    {formatDebits(decision.debits)}
                  </Typography>
                </Box>
                <Box flex="1 1 calc(50% - 8px)" minWidth="200px">
                  <Typography variant="overline" color="textSecondary">
                    MIB Codes
                  </Typography>
                  <Typography variant="body2">
                    {decision.mibCodes?.join(', ')}
                  </Typography>
                </Box>
                <Box flex="1 1 calc(50% - 8px)" minWidth="200px">
                  <Typography variant="overline" color="textSecondary">
                    AMENDMENT
                  </Typography>
                  <Typography variant="body2">
                    {formatDecisionValue(decision.amendment)}
                  </Typography>
                </Box>
                <Box flex="1 1 calc(50% - 8px)" minWidth="200px">
                  <Typography variant="overline" color="textSecondary">
                    Doctor's Info
                  </Typography>
                  <Typography variant="body2">
                    {formatDecisionValue(decision.doctorInfo)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box>
            <NoraButton
              name={ElementLocators.LOCATOR_REVIEW_APPROVAL_BUTTON}
              className={styles.DrawerButton}
              onClick={onConfirm}
            >
              {Resource.CONFIRM_DECISION}
            </NoraButton>
          </Box>
        </Drawer>
      )}
      <NoraCaption>Peer Review</NoraCaption>
      <IconLink
        onClick={() => agreeToPrelim()}
        icon={faCheck}
        copy={Resource.AGREE_WITH_PRELIM}
        locator={ElementLocators.APPROVE_DECISION_LINK}
      />
      <IconLink
        onClick={() => disagreeToPrelim()}
        icon={faTimes}
        copy={Resource.DISAGREE_WITH_PRELIM}
        locator={ElementLocators.DECLINE_DECISION_LINK}
      />
    </>
  );
}
